import { useState } from 'react'
import { useStatusStore } from '../state/statusStore'
import { type AgentState } from '../../server/agent'
import { useAuthStore } from '../state/authStore'

export function DashboardHeader() {
  const websocketConnected = useStatusStore((state) => state.websocketConnected)
  const agentState = useStatusStore((state) => state.agentState)
  const user = useAuthStore((state) => state.user)
  const setAuth = useAuthStore((state) => state.set)
  const [loggingOut, setLoggingOut] = useState(false)

  const agentIndicator = mapAgentStatus(agentState)
  const linkIndicator = mapLinkStatus(websocketConnected)

  const handleLogout = async () => {
    if (loggingOut) return
    setLoggingOut(true)
    let errorMessage: string | null = null
    try {
      const response = await fetch('/auth/logout', { method: 'POST' })
      if (!response.ok) {
        errorMessage = 'Failed to sign out. Please try again.'
      }
    } catch {
      errorMessage = 'Failed to sign out. Please try again.'
    }
    setAuth({ status: 'unauthenticated', user: null, error: errorMessage })
    setLoggingOut(false)
  }

  return (
    <header className="dashboard-header">
      <div>
        <h1 className="header-title">Agent Control Center</h1>
      </div>
      <div className="header-controls">
        <div className="status-indicators-wrapper">
          <StatusIndicator label="Agent" state={agentIndicator.state} description={agentIndicator.description} />
          <StatusIndicator label="Link" state={linkIndicator.state} description={linkIndicator.description} />
        </div>
        <div className="header-user">
          <div className="header-user-info">
            <span className="header-user-name">{user?.display_name ?? user?.email ?? 'Signed in'}</span>
            <span className="header-user-role">{user?.role === 'admin' ? 'Admin' : 'User'}</span>
          </div>
          <button
            className="btn-ghost header-logout-button"
            type="button"
            onClick={handleLogout}
            disabled={loggingOut}
          >
            {loggingOut ? 'Signing out...' : 'Sign out'}
          </button>
        </div>
      </div>
    </header>
  )
}

type StatusKind = 'active' | 'warning' | 'idle' | 'critical';

type StatusIndicatorProps = {
  label: string;
  state: StatusKind;
  description: string;
};

const STATE_TO_DOT_CLASS: Record<StatusKind, string> = {
  active: 'status-dot--positive',
  warning: 'status-dot--warning',
  idle: 'status-dot--muted',
  critical: 'status-dot--critical',
};

function StatusIndicator({ label, state, description }: StatusIndicatorProps) {
  const dotClass = STATE_TO_DOT_CLASS[state];

  return (
    <div className="status-indicator">
      <span className={`status-dot ${dotClass}`} aria-hidden />
      <div className="status-info">
        <span className="status-label">{label}</span>
        <span className="status-description">{description}</span>
      </div>
    </div>
  );
}

type IndicatorState = Pick<StatusIndicatorProps, 'state' | 'description'>;

function mapAgentStatus(agentState: AgentState): IndicatorState {
  switch (agentState) {
    case 'running':
      return {
        state: 'active',
        description: 'Running',
      };
    case 'idle':
      return {
        state: 'idle',
        description: 'Idle',
      };
    case 'error':
      return {
        state: 'critical',
        description: 'Error',
      };
  }
}

function mapLinkStatus(connected: boolean): IndicatorState {
  if (connected) {
    return {
      state: 'active',
      description: 'Connected',
    };
  }

  return {
    state: 'critical',
    description: 'Disconnected',
  };
}
